import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { civilToday, exportQueryOf, formatCivilDate, useListSearch } from '@eis/core';
import {
  ExportButton,
  InfiniteDataGrid,
  PropertyFilter,
  RequirePermission,
  ValidityPeriodSelector,
  cardSurfaceSx,
  lastDayOf,
  useGridExportColumns,
  useValidityPeriodUrlState,
  type ExportableGridColDef,
} from '@eis/patterns';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/group-policies';
import { GroupPremiumSummary } from '../../premium/ui/GroupPremiumSummary';
import { formatAmount, taxTotalOf } from '../../premium/model/format';

const MEMBER_SORT_KEYS: Readonly<Record<string, string>> = {
  policyCode: 'policyCode',
  insuredLabel: 'insuredName',
  premium: 'monthlyPremium',
  activeFrom: 'activeFrom',
};
import { CreatePolicyDialog } from '../../policies/ui/CreatePolicyDialog';
import { InheritedValue } from './InheritedValue';

/**
 * Policen tab — the member PROJECTION, and it is read-only by construction.
 *
 * Not a design choice: `ListGroupPolicyMembers` returns `MemberProjection`, which carries no
 * writable identity, because GroupPolicyService topologically has no RPC that writes a child
 * (decisions D5/D7, architecture §B.2). A member is endorsed, corrected, terminated or detached
 * through PolicyService — the Policen surface — one member at a time, by whoever holds that
 * scope. Offering an edit here would be the console inventing a coupling the API deliberately
 * does not have.
 *
 * The policyholder column is the one that carries a claim about the model. Every member's
 * policyholder is the master's, resolved at read (decision D7): the value would look identical to
 * a local one, and the difference is that nobody can change it here. The marker says so, in the
 * operations persona's words — "Von Gruppen-Police geerbt".
 */
export function GroupPolicyPoliciesTab({
  code,
  policyholderLabel,
  allowedTarifs,
  memberSchema,
  locale,
}: {
  readonly code: string;
  /** The master's policyholder as the header shows it: name when resolved, id otherwise. */
  readonly policyholderLabel: string;
  readonly allowedTarifs: readonly string[];
  /** What a member is validated against, through the agreement's pin (SDD-011 P-8). */
  readonly memberSchema: {
    readonly data?: InstanceApi.GetGroupPolicyMemberSchemaResponse;
    readonly isPending: boolean;
    readonly isError: boolean;
  };
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  /*
    What this tab is answering about, in time (SDD-021 §9): ONE day — *Stichtag*, today unless
    chosen — or a PERIOD — *Zeitraum*, every member covered at any time in it. The description says
    which, because "1 of 1" is otherwise indistinguishable from "this agreement has one member" when
    six more start next month.

    Local to this tab rather than on the detail header: the members list and its premium are the
    only reads on this page with a business date. Since SDD-021 the state is the URL's (rule 8d):
    the day or the period, the filter, the search and the order — a members view is a link.
  */
  const url = useListSearch();
  const period = useValidityPeriodUrlState();
  const validity = period.value;
  const activeOn = validity.kind === 'day' ? (validity.validAt ?? civilToday()) : undefined;
  const catalog = useFilterCatalog('groupPolicyMembers', { code });
  const members = useInfiniteList(
    'groupPolicyMembers',
    {
      ...(validity.kind === 'period'
        ? { coveredFrom: validity.from, coveredTo: validity.to }
        : { validAt: activeOn }),
      filters: url.params.filters,
      search: url.params.search,
      order: url.params.order,
      includeTotal: true,
    },
    { code },
  );
  const [creating, setCreating] = useState(false);

  const rows = useMemo(
    () =>
      members.items.map((member: InstanceApi.MemberProjection) => ({
        id: member.policyCode ?? '',
        policyCode: member.policyCode ?? '',
        insuredPartnerId: member.insuredPartnerId ?? '',
        // The insured OBJECT as the service projects it (SDD-022 D-4): plate · VIN for a car, the
        // frame number for a bike. An object-only member has no insured person — that cell stays
        // empty rather than "unresolved", and this one carries the vehicle.
        objectIdentifier: member.objectIdentifier ?? '',
        // The projection names the insured person (SDD-014 F-8); the id when nobody knew (SSOT §4.2).
        insuredLabel:
          member.insuredPartnerNameResolved === true && (member.insuredPartnerName ?? '') !== ''
            ? (member.insuredPartnerName ?? '')
            : (member.insuredPartnerId ?? ''),
        tarifVersionErn: member.tarifVersionErn ?? '',
        activeFrom: member.activeFrom ?? '',
        // The member's monthly net, tax and gross (SDD-021 §9), each the service's figure through
        // formatCurrency. Gross is `monthlyPremium`, the sortable one.
        net: formatAmount(member.monthly?.net, member.monthlyPremium?.currency, locale),
        tax: formatAmount(taxTotalOf(member.monthly), member.monthlyPremium?.currency, locale),
        premium: formatAmount(
          member.monthlyPremium?.amount,
          member.monthlyPremium?.currency,
          locale,
        ),
      })),
    [members.items, locale],
  );

  // Paths in `MemberProjection` (SDD-020 §8.1): the export of this tab is the member export with
  // the totals row (J-10); the inherited policyholder is the master's and no member column.
  const columns = useMemo<ExportableGridColDef<(typeof rows)[number]>[]>(
    () => [
      {
        field: 'policyCode',
        headerName: t('groupPolicies.policies.columns.id'),
        width: 140,
        exportPath: 'policyCode',
      },
      {
        field: 'objectIdentifier',
        headerName: t('groupPolicies.policies.columns.insuredObject'),
        flex: 1.2,
        minWidth: 230,
        sortable: false,
        exportPath: 'objectIdentifier',
        valueFormatter: (value: string) => (value === '' ? '—' : value),
      },
      {
        field: 'insuredLabel',
        headerName: t('groupPolicies.policies.columns.insuredPerson'),
        flex: 1,
        minWidth: 190,
        exportPath: 'insuredPartnerName',
      },
      {
        field: 'policyholder',
        headerName: t('groupPolicies.policies.columns.policyholder'),
        flex: 1.2,
        minWidth: 260,
        sortable: false,
        renderCell: (): ReactNode => (
          <InheritedValue value={policyholderLabel} source="inherited" />
        ),
      },
      {
        field: 'net',
        headerName: t('groupPolicies.policies.columns.net'),
        width: 130,
        // The member export carries the premium model (SDD-021 × SDD-020 J-10): net, tax, gross.
        exportPath: 'monthly.net',
        exportType: 'MONEY',
        align: 'right',
        headerAlign: 'right',
        sortable: false,
      },
      {
        field: 'tax',
        headerName: t('groupPolicies.policies.columns.tax'),
        width: 130,
        exportPath: 'monthly.taxTotal',
        exportType: 'MONEY',
        align: 'right',
        headerAlign: 'right',
        sortable: false,
      },
      {
        field: 'premium',
        headerName: t('groupPolicies.policies.columns.premium'),
        width: 140,
        exportPath: 'monthlyPremium',
        exportType: 'MONEY',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'activeFrom',
        headerName: t('groupPolicies.policies.columns.validFrom'),
        width: 140,
        exportPath: 'activeFrom',
        exportType: 'DATE',
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
    ],
    [t, locale, policyholderLabel],
  );
  const exportColumns = useGridExportColumns(columns);

  // The day or the period, as the reader says it: a period by its first and LAST day.
  const during =
    validity.kind === 'period'
      ? {
          from: formatCivilDate(validity.from, locale),
          to: formatCivilDate(lastDayOf(validity.to), locale),
        }
      : undefined;

  return (
    <Stack spacing={2.5}>
      {/*
        The premium of ALL members, computed by the service for the same day or period as the
        list (SDD-021 §8.1) — never a sum over the rows the scroll has loaded.
      */}
      <GroupPremiumSummary
        code={code}
        basis={
          validity.kind === 'period'
            ? { periodFrom: validity.from, periodTo: validity.to }
            : { validAt: activeOn }
        }
        locale={locale}
      />

      {/* Dunning is master-only by domain rule (SSOT I-20) — said where the members are. */}
      <Alert severity="warning">{t('groupPolicies.policies.dunningNote')}</Alert>

      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" component="h2">
                {t('groupPolicies.policies.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('groupPolicies.policies.description')}
              </Typography>
              {/*
                Names the date the grid is about. Without it the count is ambiguous in exactly the
                way that hides future members, and the Leads tab's own count appears to contradict it.
              */}
              <Typography variant="body2" color="text.secondary" data-testid="members-active-on">
                {during === undefined
                  ? t('groupPolicies.policies.activeOn', {
                      date: formatCivilDate(activeOn ?? civilToday(), locale),
                    })
                  : t('groupPolicies.policies.activeDuring', during)}
              </Typography>
            </Box>
            {/*
              Enrolling a member is a POLICY write (PolicyService.CreatePolicy), not a master write:
              the master is named as the parent and nothing about it changes (D5/D7). The action
              lives here because this is where an operator looks at the population — and it is
              gated by `policies.create`, the instance layer's own action.
            */}
            <Stack direction="row" spacing={1}>
              {/*
                The export is the list on ONE day (SDD-020 J-8: `ExportQuery` carries `validAt`, no
                covered period yet). On a *Zeitraum* it would export another list than the screen
                shows, so it is offered on the *Stichtag* only (SDD-021 §14).
              */}
              {validity.kind === 'day' && (
                <ExportButton
                  service="insurance"
                  permission={p.exports.create}
                  rowCount={members.isLoading ? undefined : rows.length}
                  buildRequest={(format) => ({
                    collection: 'group-policy-members',
                    parent: code,
                    query: exportQueryOf(
                      {
                        filters: url.params.filters ?? '',
                        search: url.params.search ?? '',
                        order: url.params.order,
                      },
                      { validAt: members.cut?.validAt ?? activeOn, asOf: members.cut?.asOf },
                    ),
                    columns: exportColumns,
                    format,
                    label: t('groupPolicies.policies.exportLabel', { code }),
                  })}
                />
              )}
              <RequirePermission anyOf={[p.policies.create]}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  {t('policies.create.action')}
                </Button>
                <CreatePolicyDialog
                  parentPolicyCode={code}
                  allowedTarifs={allowedTarifs}
                  memberSchema={memberSchema}
                  open={creating}
                  onClose={() => {
                    setCreating(false);
                  }}
                />
              </RequirePermission>
            </Stack>
          </Stack>

          {/*
            *Stichtag* or *Zeitraum* — written to the URL; a cleared day falls back to today rather
            than silently dropping the parameter ("no date" is not a question this list answers).
          */}
          <ValidityPeriodSelector
            value={validity}
            onChange={period.set}
            dayLabel={t('groupPolicies.policies.activeOnLabel')}
            dayHelperText={t('groupPolicies.policies.activeOnHelp')}
            locale={locale}
          />

          {/* Said once, plainly, rather than implied by the absence of buttons. */}
          <Alert severity="info" icon={<LinkIcon fontSize="inherit" />}>
            <Tooltip title={t('groupPolicies.policies.readOnlyHint')}>
              <span>{t('groupPolicies.policies.readOnly')}</span>
            </Tooltip>
          </Alert>

          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
          />
          <InfiniteDataGrid
            rows={rows}
            list={members}
            columns={columns}
            ariaLabel={t('groupPolicies.policies.title')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={MEMBER_SORT_KEYS}
            showCut
            // Date-aware on purpose: "there are no policies yet" is a claim about the agreement,
            // and all this grid knows is that none were active on one day.
            emptyDescription={
              during === undefined
                ? t('groupPolicies.policies.empty', {
                    date: formatCivilDate(activeOn ?? civilToday(), locale),
                  })
                : t('groupPolicies.policies.emptyDuring', during)
            }
          />
        </Stack>
      </Paper>
    </Stack>
  );
}
